import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFlexibleMemoizedCallback } from 'pxc-editor-core/helpers/reactHooks';
import { 
  getElementByQuerySelector,
  getElementsByQuerySelectorAll,
  isRightMouseButtonClicked,
  checkCtrlPressed,
  SectionEntity
} from '@/helpers';
import { addMultipleSectionsThunk, handleSectionClick, clearSelectedSections } from '@/actions';
import { KEY_ESC } from '@/constants';

const ShieldsDragSelection = (props) => {
  const { svgRendererSelector, isUserManipulatorTouch, visiblePagesUids, pages } = props;
  const dispatch = useDispatch();
  const sectionsSelection = useSelector(state => state.core.sectionsSelection);
  const dragRectElRef = useRef();
  const svgPointElRef = useRef();
  const rendererSvgElRef = useRef(null);
  const dragStartPointRef = useRef(null);
  const sectionsRef = useRef([]);

  useEffect(() => {
    rendererSvgElRef.current = getElementByQuerySelector(svgRendererSelector);
    if (rendererSvgElRef.current) {
      svgPointElRef.current = svgPointElRef.current || rendererSvgElRef.current.createSVGPoint();

      rendererSvgElRef.current.addEventListener('mousedown', onMouseDown);
      rendererSvgElRef.current.addEventListener('mousemove', onMouseMove);
      rendererSvgElRef.current.addEventListener('mouseup', onMouseUp);
      rendererSvgElRef.current.addEventListener('mouseleave', onMouseUp);

      rendererSvgElRef.current.addEventListener('touchstart', onMouseDown);
      rendererSvgElRef.current.addEventListener('touchmove', onMouseMove);
      rendererSvgElRef.current.addEventListener('touchend', onMouseUp);
      rendererSvgElRef.current.addEventListener('touchcancel', onMouseUp);
    }

    return () => {
      if (rendererSvgElRef.current){
        rendererSvgElRef.current.removeEventListener('mousedown', onMouseDown);
        rendererSvgElRef.current.removeEventListener('mousemove', onMouseMove);
        rendererSvgElRef.current.removeEventListener('mouseup', onMouseUp);
        rendererSvgElRef.current.removeEventListener('mouseleave', onMouseUp);

        rendererSvgElRef.current.removeEventListener('touchstart', onMouseDown);
        rendererSvgElRef.current.removeEventListener('touchmove', onMouseMove);
        rendererSvgElRef.current.removeEventListener('touchend', onMouseUp);
        rendererSvgElRef.current.removeEventListener('touchcancel', onMouseUp);
      }
    }
  }, []);

  useEffect(() => {
    visiblePagesUids.forEach(pageId => {
      sectionsRef.current = [...Object.values(pages[pageId].sections)];
    });
  }, [ pages, visiblePagesUids ]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    }
  }, []);

  const handleKeydown = (event) => {
    if (event.code === KEY_ESC) {
      handleDragSelectionReset();
    }
  };

  const getCursorPoint = (event) => {
    svgPointElRef.current.x = event.clientX || event.changedTouches[0].clientX;
    svgPointElRef.current.y = event.clientY || event.changedTouches[0].clientY;
    const cursorPoint = svgPointElRef.current.matrixTransform(
      rendererSvgElRef.current.getScreenCTM().inverse(),
    );
    return cursorPoint;
  };

  const updateDragRect = (dragRectAttributes) => {
    if (dragRectElRef.current) {
      for (const attr in dragRectAttributes) {
        dragRectElRef.current.setAttribute([attr], dragRectAttributes[attr]);
      }
    }
  };

  const isDraggableInParentTree = (elem) => {
    let parElem = elem?.parentElement;
    if (parElem) {
      const parElemClasses = ('' + parElem.classList?.value).split(' ');
      if (parElemClasses?.find(n => n === 'draggable')) {
        return true;
      } else {
        return isDraggableInParentTree(parElem);
      }
    } else {
      return false;
    }
  }

  const onMouseDown = useFlexibleMemoizedCallback((event) => {
    const tarElem = event.target;
    const cursorPoint = getCursorPoint(event);

    // when touching with two fingers
    if (event?.touches?.length > 1) {
      return;
    }
    // when ctrl is pressed
    if (checkCtrlPressed(event)) {
      return;
    }
    // when a section is hovered and drag is happening then just select the section
    if (SectionEntity.isHovered && !SectionEntity.shield?.isMatrix) {
      return dispatch(handleSectionClick({ section: SectionEntity.section }));
    }
    // when right mouse button is clicked
    if (isRightMouseButtonClicked(event)) {
      return;
    }
    // when draggable element selected
    // when shield selected
    if (!!Object.keys(sectionsSelection).length) {
      const tarElemClasses = ('' + tarElem.classList?.value).split(' ');
      const isDraggable = tarElemClasses?.find(n => n === 'draggable' || n === 'select-area-dot' || n === 'section-area' || n === 'matrixselection_corner_path')?.length > 0;
      const isDraggableElement = isDraggableInParentTree(tarElem);
      const isShieldSelected = tarElemClasses.some(n => n === 'shield');

      if (isDraggable || isDraggableElement || isShieldSelected) {
        return;
      }
    }

    updateDragRect({ x: cursorPoint.x, y: cursorPoint.y, });

    dragStartPointRef.current = { x: cursorPoint.x, y: cursorPoint.y };

    dispatch(clearSelectedSections());
  });

  const onMouseMove = useFlexibleMemoizedCallback((event) => {
    if (event?.touches?.length > 1) {
      return;
    }

    if (isRightMouseButtonClicked(event)) {
      return;
    }

    if (dragStartPointRef.current) {
      const cursorPoint = getCursorPoint(event);
      const width = cursorPoint.x - dragStartPointRef.current.x;
      const height = cursorPoint.y - dragStartPointRef.current.y;
      const dragRectAttr = { width, height, style: 'visibility: visible' };

      if (width < 0) {
        dragRectAttr.width *= -1;
        dragRectAttr.x = dragStartPointRef.current.x - dragRectAttr.width;
      }
      if (height < 0) {
        dragRectAttr.height *= -1;
        dragRectAttr.y = dragStartPointRef.current.y - dragRectAttr.height;
      }

      updateDragRect(dragRectAttr);

      const dragRectElRect = dragRectElRef.current.getBoundingClientRect();

      for (const section of sectionsRef.current) {
        const sectionElement = getElementByQuerySelector(`#${section.id}`);
        const scissorsShellElement = getElementsByQuerySelectorAll(`.${section.id}_scissors-shell`);
        const sectionElementRect = sectionElement.getBoundingClientRect();

        if (detectCollision(dragRectElRect, sectionElementRect)) {
          sectionElement.classList.add('selected-fill');
          scissorsShellElement?.forEach((element) => element.classList.add('selected-fill'));
        } else {
          sectionElement.classList.remove('selected-fill');
          scissorsShellElement?.forEach((element) => element.classList.remove('selected-fill'));
        }
      }
    }
  });

  const onMouseUp = useFlexibleMemoizedCallback((event) => {
    if (event?.touches?.length > 1) {
      return;
    }

    if (checkCtrlPressed(event) || isRightMouseButtonClicked(event)) {
      handleDragSelectionReset();
      return;
    }

    if (dragStartPointRef.current) {
      updateDragRect({ x: 0, y: 0, width: 0, height: 0, style: 'visibility: hidden' });

      const selectedSections = [...getElementsByQuerySelectorAll('.selected-fill')].reduce((acc, element) => {
        element.classList.remove('selected-fill');

        const sectionId = element.getAttribute('id');
        const section = sectionsRef.current.find(sec => sec.id === sectionId);

        if (section) {
          return { ...acc, [section.id]: section };
        }
        return acc;
      }, {});

      if (!!Object.keys(selectedSections).length) {
        dispatch(addMultipleSectionsThunk({ selectedSections }));
      }
    }

    dragStartPointRef.current = null;
  });

  const handleDragSelectionReset = () => {
    const selectedElements = getElementsByQuerySelectorAll('.selected-fill');

    if (!!selectedElements.length) {
      selectedElements.forEach(element => element.classList.remove('selected-fill'));
    }

    updateDragRect({ x: 0, y: 0, width: 0, height: 0, style: 'visibility: hidden' });

    dragStartPointRef.current = null;
  }

  return (
    <rect
      ref={dragRectElRef}
      x="0"
      y="0"
      width="0"
      height="0"
      fill="rgba(245,245,245,0.6)"
      stroke="#007981"
      strokeDasharray="5,5"
    />
  );
};

export default React.memo(ShieldsDragSelection);

const detectCollision = (rect1, rect2) =>
  rect1.x < rect2.x + rect2.width &&
  rect1.x + rect1.width > rect2.x &&
  rect1.y < rect2.y + rect2.height &&
  rect1.y + rect1.height > rect2.y;
